import { useMemo, useState } from 'react';
import {
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { usePrefetch } from 'utils/wemble-api';
import { haptic } from 'utils/haptics';
import { CompetenceDto } from 'utils/wemble-api.generated';
import {
  ALL_CATEGORIES,
  CategoryNode,
  categorySections,
  pathOf,
  subtreeLeaves,
} from 'components/shared/UserCard/competenceTree';
import SkillLevelTrack from 'components/shared/SkillLevelTrack';
import { ALL, Selection, UNCATEGORIZED } from './CategoryTree';

const useStyles = makeStyles((theme) => ({
  card: {
    backgroundColor: 'white',
    borderRadius: 16,
    padding: 24,
    [theme.breakpoints.down('xs')]: {
      padding: 16,
    },
  },
  breadcrumb: {
    fontSize: 13,
    color: theme.palette.gray.g2,
    marginBottom: 6,
  },
  crumb: {
    border: 'none',
    background: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    cursor: 'pointer',
    minHeight: 32, // comfortable tap target for the phone back link
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const, // no iOS long-press menu
    userSelect: 'none' as const,
    '@media (hover: hover)': {
      '&:hover': {
        color: '#38566B',
        textDecoration: 'underline',
      },
    },
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  title: {
    fontSize: 24,
    fontWeight: 600,
    margin: 0,
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down('xs')]: {
      fontSize: 20,
      // Own line on phones so a long name never runs under the buttons.
      flexBasis: '100%',
    },
  },
  actions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  button: {
    height: 36,
    padding: '0 14px',
    borderRadius: 8,
    border: 'none',
    fontSize: 14,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.gray.g7,
    color: theme.palette.gray.g1,
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const, // no iOS long-press menu
    userSelect: 'none',
    // Touch: pressed state instead of a hover that would stick after a tap.
    '&:active': {
      backgroundColor: theme.palette.gray.g6,
    },
  },
  search: {
    width: '100%',
    boxSizing: 'border-box',
    height: 40,
    borderRadius: 8,
    border: 'none',
    backgroundColor: theme.palette.gray.g3,
    padding: '0 14px',
    fontSize: 14,
    fontFamily: 'inherit',
    outline: 'none',
    margin: '16px 0 8px',
  },
  // Phones only: categories to drill into, since there is no sidebar.
  subRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minHeight: 48,
    padding: '0 12px',
    borderRadius: 10,
    backgroundColor: theme.palette.gray.g7,
    marginBottom: 6,
    fontSize: 15,
    fontWeight: 500,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none' as const, // no iOS long-press menu
    userSelect: 'none',
    // Touch: pressed state instead of a hover that would stick after a tap.
    '&:active': {
      backgroundColor: theme.palette.gray.g6,
    },
  },
  subRow__name: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  subRow__count: {
    fontSize: 13,
    color: theme.palette.gray.g2,
    fontWeight: 400,
  },
  table: {
    // Fixed columns so People / Avg. level line up from card to card.
    tableLayout: 'fixed',
    // The row hover reaches 12px past the text on both sides, with rounded
    // ends: the table is pulled out by the padding it adds, so the text stays
    // aligned with the search field. Separate borders so cells can round.
    borderCollapse: 'separate',
    borderSpacing: 0,
    margin: '0 -12px',
    width: 'calc(100% + 24px)',
    '& .MuiTableCell-root:first-child': {
      paddingLeft: 12,
    },
    '& .MuiTableCell-root:last-child': {
      paddingRight: 12,
    },
    '& .MuiTableRow-root': {
      WebkitTapHighlightColor: 'transparent',
      WebkitTouchCallout: 'none' as const, // no iOS long-press menu
      // Names stay selectable with a mouse; on touch a long press would
      // start text selection instead of opening the row.
      '@media (pointer: coarse)': {
        userSelect: 'none' as const,
      },
    },
    '& .MuiTableRow-hover:hover > td:first-child': {
      borderTopLeftRadius: 8,
      borderBottomLeftRadius: 8,
    },
    '& .MuiTableRow-hover:hover > td:last-child': {
      borderTopRightRadius: 8,
      borderBottomRightRadius: 8,
    },
    '& .MuiTableCell-root': {
      borderBottomColor: theme.palette.gray.g6,
      padding: '12px 8px 12px 0',
    },
    '& .MuiTableCell-head': {
      fontSize: 12,
      textTransform: 'uppercase',
      color: theme.palette.gray.g10,
      padding: '8px 8px 8px 0',
    },
    '& .MuiTableCell-body': {
      fontWeight: 500,
    },
    '& .MuiTableBody-root .MuiTableRow-root:nth-last-child(1) > td': {
      border: 'none',
    },
  },
  stack: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    minWidth: 0,
  },
  groupHeader: {
    fontSize: 17,
    fontWeight: 600,
    color: theme.palette.gray.g1,
    marginBottom: 4,
  },
  groupHeader_link: {
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    WebkitTouchCallout: 'none',
    userSelect: 'none',
    '@media (hover: hover)': {
      '&:hover': {
        color: '#38566B',
      },
    },
    '&:active': {
      color: '#38566B',
    },
  },
  where: {
    fontSize: 12,
    color: theme.palette.gray.g2,
    fontWeight: 400,
  },
  empty: {
    fontSize: 14,
    color: theme.palette.gray.g2,
    padding: '24px 0 8px',
    textAlign: 'center',
  },
}));

/** The selected category (or everything / uncategorized) and every competency
 *  underneath it. Subcategories live in the sidebar; on phones, where there is
 *  no sidebar, they are listed here to drill into. */
const CategoryDetail = ({
  selection,
  node,
  tops,
  orphans,
  all,
  onSelect,
  canManageCategories,
  canEditCompetences,
  onEditCategory,
  onCreateCategory,
  onCreateCompetence,
}: {
  selection: Selection;
  node?: CategoryNode;
  tops: CategoryNode[];
  orphans: CompetenceDto[];
  all: CompetenceDto[];
  onSelect: (selection: Selection) => void;
  canManageCategories: boolean;
  canEditCompetences: boolean;
  onEditCategory: () => void;
  onCreateCategory: () => void;
  onCreateCompetence: () => void;
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  // Row hover only where a pointer can hover; on touch it sticks after a tap.
  const canHover = useMediaQuery('(hover: hover)');
  // Navigation taps get the app's light haptic, like the tab bar.
  const go = (target: Selection) => {
    haptic('light');
    onSelect(target);
  };
  const history = useHistory();
  const prefetchCompetence = usePrefetch('getCompetence');
  const [query, setQuery] = useState('');

  const title = node ? node.name : selection === UNCATEGORIZED ? 'Uncategorized' : 'All competencies';

  const rows = useMemo(() => {
    const scope = node ? subtreeLeaves(node) : selection === UNCATEGORIZED ? orphans : all.filter((c) => !c.root);
    const q = query.trim().toLowerCase();
    return scope
      .filter((c) => !q || (c.name || '').toLowerCase().includes(q))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [node, selection, orphans, all, query]);

  // A competency's full category path ("Rettsområder › Bank og finans"), the
  // same wherever you are. Inside a category without subcategories every row
  // would just repeat the selection, so the column is hidden there instead of
  // showing blanks.
  const whereOf = (competence: CompetenceDto) => {
    const parentId = (competence.parent as { _id?: string | null } | null | undefined)?._id;
    const find = (list: CategoryNode[]): CategoryNode | undefined => {
      for (const n of list) {
        if (n.id === parentId) return n;
        const hit = find(n.children);
        if (hit) return hit;
      }
      return undefined;
    };
    const parent = parentId ? find(tops) : undefined;
    return parent
      ? pathOf(parent)
          // Non-breaking spaces inside names: a long path wraps between
          // categories, not in the middle of one.
          .map((n) => n.name.replace(/ /g, '\u00A0'))
          .join(' › ')
      : '';
  };
  // Hidden where it would be blank on every row: inside a category without
  // subcategories, in Uncategorized, or at a firm without categories.
  // The list is split into sections per category/subcategory (same headings as
  // the profile card's grid), so the path column is only needed for search
  // results, which are one flat list.
  const searching = !!query.trim();
  const showCategory =
    searching && selection !== UNCATEGORIZED && tops.length > 0 && !(node && node.children.length === 0);
  const groups = useMemo((): { label: string | null; items: CompetenceDto[] }[] => {
    if (searching || selection === UNCATEGORIZED) return [{ label: null, items: rows }];
    const byName = (a: CompetenceDto, b: CompetenceDto) => (a.name || '').localeCompare(b.name || '');
    const sections = categorySections(all, node ? node.id : ALL_CATEGORIES);
    return sections.map((section) => ({
      // Inside a category its own competencies have no label; name them after
      // the category when subcategories follow. "Other" reads "Uncategorized"
      // here, like the sidebar.
      label:
        section.label === 'Other' && !node
          ? 'Uncategorized'
          : section.label ?? (node && sections.length > 1 ? node.name : null),
      items: [...section.competences].sort(byName),
    }));
  }, [searching, selection, rows, all, node]);

  // Phones: the top level is a list of categories to drill into — unless the
  // firm has none, then the competencies are shown straight away.
  // Each part of a card heading ("Rettsområder › Bank og finans") opens its own
  // category. The group's category is the parent of its competencies; the
  // heading shows the last N levels of that category's path.
  const nodeById = useMemo(() => {
    const map = new Map<string, CategoryNode>();
    const walk = (n: CategoryNode) => {
      map.set(n.id, n);
      n.children.forEach(walk);
    };
    tops.forEach(walk);
    return map;
  }, [tops]);
  const headingParts = (group: { label: string | null; items: CompetenceDto[] }) => {
    const names = (group.label || '').split(' · ');
    if (group.label === 'Uncategorized') return [{ name: group.label, target: UNCATEGORIZED as Selection }];
    const parentId = (group.items[0]?.parent as { _id?: string | null } | null | undefined)?._id;
    const groupNode = parentId ? nodeById.get(parentId) : undefined;
    const path = groupNode ? pathOf(groupNode).slice(-names.length) : [];
    return names.map((name, index) => ({ name, target: (path[index]?.id ?? null) as Selection }));
  };

  const drillIn = narrow && !query ? (node ? node.children : selection === ALL ? tops : []) : [];
  const showList = !(narrow && selection === ALL && !query && tops.length > 0);
  // Categories are two levels deep, so adding one only makes sense at the top
  // or inside a top-level category.
  const canAddHere = !node || !node.parent;

  return (
    <div className={classes.stack}>
      <section className={classes.card}>
        {/* Desktop has the sidebar to show where you are; phones get one step back. */}
        {narrow && selection !== ALL && (
          <div className={classes.breadcrumb}>
            <button className={classes.crumb} onClick={() => go(node?.parent?.id ?? ALL)}>
              ‹ {node?.parent?.name ?? 'All competencies'}
            </button>
          </div>
        )}
        <div className={classes.titleRow}>
          <h1 className={classes.title}>{title}</h1>
          <div className={classes.actions}>
            {canEditCompetences && (
              <button className={classes.button} onClick={onCreateCompetence}>
                + Add competency
              </button>
            )}
            {canManageCategories && node && (
              <button className={classes.button} onClick={onEditCategory}>
                Edit category
              </button>
            )}
          </div>
        </div>

        <input
          className={classes.search}
          placeholder={node ? `Search in ${node.name}` : 'Search competencies'}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        {drillIn.map((child) => (
          <div key={child.id} className={classes.subRow} onClick={() => go(child.id)}>
            <span className={classes.subRow__name}>{child.name}</span>
            <span className={classes.subRow__count}>{subtreeLeaves(child).length}</span>
            <span>›</span>
          </div>
        ))}
        {narrow && selection === ALL && !query && tops.length > 0 && orphans.length > 0 && (
          <div className={classes.subRow} onClick={() => go(UNCATEGORIZED)}>
            <span className={classes.subRow__name} style={{ fontStyle: 'italic', color: theme.palette.gray.g2 }}>
              Uncategorized
            </span>
            <span className={classes.subRow__count}>{orphans.length}</span>
            <span>›</span>
          </div>
        )}
        {/* Phones have no sidebar, so adding a category sits at the end of
          this list, where the sidebar would have it. */}
        {narrow && !query && canManageCategories && selection !== UNCATEGORIZED && canAddHere && (
          <div className={classes.subRow} style={{ color: '#38566B' }} onClick={onCreateCategory}>
            <span className={classes.subRow__name}>+ Add category</span>
          </div>
        )}
      </section>

      {/* Each category/subcategory is its own card on the grey page, so groups
          read as separate blocks rather than one long list. */}
      {showList &&
        (rows.length === 0 ? (
          <section className={classes.card}>
            <div className={classes.empty}>
              {query ? 'No competencies match your search.' : 'No competencies here yet.'}
            </div>
          </section>
        ) : (
          groups.map((group, groupIndex) => {
            return (
              <section key={`${groupIndex}-${group.label ?? ''}`} className={classes.card}>
                {group.label && (
                  <div className={classes.groupHeader}>
                    {headingParts(group).map((part, index) => (
                      <span key={index}>
                        {index > 0 && ' › '}
                        {part.target && part.target !== selection ? (
                          <span className={classes.groupHeader_link} onClick={() => go(part.target)}>
                            {part.name}
                          </span>
                        ) : (
                          part.name
                        )}
                      </span>
                    ))}
                  </div>
                )}
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      {!narrow && showCategory && <TableCell style={{ width: '32%' }}>Category</TableCell>}
                      {!narrow && (
                        <TableCell align="right" style={{ width: 90 }}>
                          People
                        </TableCell>
                      )}
                      <TableCell align="right" style={{ width: narrow ? 110 : 130 }}>
                        Avg. level
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.items.map((competence) => {
                      const id = competence._id || '';
                      const where = whereOf(competence);
                      return (
                        <TableRow
                          key={id}
                          hover={canHover}
                          style={{ cursor: 'pointer' }}
                          onClick={() => history.push(`/competences/${id}`)}
                          onMouseEnter={() => id && prefetchCompetence({ id }, { ifOlderThan: 180 })}
                        >
                          <TableCell>
                            {competence.name}
                            {narrow && showCategory && where && <div className={classes.where}>{where}</div>}
                          </TableCell>
                          {!narrow && showCategory && <TableCell className={classes.where}>{where}</TableCell>}
                          {!narrow && <TableCell align="right">{competence.users?.length ?? 0}</TableCell>}
                          <TableCell align="right">
                            <div style={{ display: 'inline-flex' }}>
                              <SkillLevelTrack
                                size="inline"
                                level={
                                  competence.averageSkillLevel === null || competence.averageSkillLevel === undefined
                                    ? -1
                                    : Math.round(competence.averageSkillLevel)
                                }
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </section>
            );
          })
        ))}
    </div>
  );
};

export default CategoryDetail;
